// Единое поведение всех модальных окон без правки каждого из ~57 оверлеев (по тому же принципу, что utils/a11y.js):
//   · role="dialog" и aria-modal, подпись из первого заголовка окна;
//   · фокус входит в окно при открытии, Tab не уходит за его пределы, при закрытии фокус возвращается на кнопку, открывшую окно;
//   · Esc закрывает верхнее окно (нажатием на его подложку, так же как закрывает клик мимо окна: если окно не закрывается кликом мимо, Esc его не трогает).
// Окна, которые сами управляют клавишами (поиск Ctrl+K, экран блокировки, всплывающий звонок), помечаются data-dialog-skip или не попадают под критерии.

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const stack = [];            // открытые окна: верхнее последнее
const returnFocus = new WeakMap();

// Окно = закреплённый слой поверх страницы (z ≥ 40), занимающий почти весь экран и содержащий элементы управления.
// Так распознаются и центральные модалки (fixed inset-0), и нижние листы, и боковые панели, независимо от набора классов.
function isDialogOverlay(el) {
  if (!(el instanceof HTMLElement) || el.hasAttribute('data-dialog-skip')) return false;
  const cs = getComputedStyle(el);
  if (cs.position !== 'fixed' || cs.pointerEvents === 'none') return false;       // всплывающая панель звонка не окно
  const z = parseInt(cs.zIndex, 10);
  if (!(z >= 40) || z >= 9000) return false;                                      // невидимые «ловушки кликов» (z-10), экран блокировки (z-[9999])
  const r = el.getBoundingClientRect();
  if (r.width < window.innerWidth * 0.9 || r.height < window.innerHeight * 0.8) return false;   // нижняя навигация и т.п.
  return !!el.querySelector('button, input, select, textarea, a[href]');         // пустая подложка без содержимого не окно
}

function focusables(root) {
  return [...root.querySelectorAll(FOCUSABLE)].filter((e) => e.offsetParent !== null || e === document.activeElement);
}

function enhance(overlay) {
  if (overlay.hasAttribute('data-dialog-ready')) return;
  overlay.setAttribute('data-dialog-ready', '1');
  if (!overlay.getAttribute('role')) overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  if (!overlay.getAttribute('aria-label') && !overlay.getAttribute('aria-labelledby')) {
    const h = overlay.querySelector('h1, h2, h3, h4, [data-dialog-title]');
    const text = h && h.textContent.trim();
    if (text) overlay.setAttribute('aria-label', text.slice(0, 80));
  }
  returnFocus.set(overlay, document.activeElement instanceof HTMLElement ? document.activeElement : null);
  stack.push(overlay);
  // начальный фокус: поле ввода окна, иначе первая кнопка, кроме «закрыть»; если фокус уже внутри (autoFocus), не трогаем
  if (!overlay.contains(document.activeElement)) {
    requestAnimationFrame(() => {
      if (!overlay.isConnected || overlay.contains(document.activeElement)) return;
      const list = focusables(overlay);
      const target = list.find((e) => /^(INPUT|TEXTAREA|SELECT)$/.test(e.tagName)) || list.find((e) => !/close|закры|yopish|kapat/i.test(e.getAttribute('aria-label') || '')) || list[0];
      if (target) target.focus({ preventScroll: true });
      else { overlay.setAttribute('tabindex', '-1'); overlay.focus({ preventScroll: true }); }
    });
  }
}

function release(overlay) {
  const i = stack.indexOf(overlay);
  if (i >= 0) stack.splice(i, 1);
  const back = returnFocus.get(overlay);
  returnFocus.delete(overlay);
  if (back && back.isConnected && !stack.length) back.focus({ preventScroll: true });
}

function scan(node) {
  if (!(node instanceof HTMLElement)) return;
  if (isDialogOverlay(node)) enhance(node);
  node.querySelectorAll && node.querySelectorAll('.fixed').forEach((el) => { if (isDialogOverlay(el)) enhance(el); });
}

function onKeyDown(e) {
  const top = stack[stack.length - 1];
  if (!top) return;
  if (!top.isConnected) { release(top); return; }
  if (e.key === 'Escape' && !e.defaultPrevented) {
    // открыт выпадающий список/меню внутри окна: Esc закрывает его (это делает сам компонент), а не окно
    if (top.querySelector('[aria-expanded="true"], [role="menu"], [role="listbox"]')) return;
    // закрываем так же, как клик мимо окна: сначала явная подложка, иначе сам оверлей
    const backdrop = top.querySelector(':scope > .absolute.inset-0') || top;
    ['mousedown', 'mouseup', 'click'].forEach((type) => backdrop.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true })));
  } else if (e.key === 'Tab') {
    const list = focusables(top);
    if (!list.length) { e.preventDefault(); return; }
    const first = list[0], last = list[list.length - 1];
    if (!top.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
    else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
}

let started = false;
export function startDialogA11y() {
  if (started || typeof document === 'undefined') return;
  started = true;
  scan(document.body);
  new MutationObserver((records) => {
    for (const r of records) {
      r.addedNodes.forEach(scan);
      r.removedNodes.forEach((n) => {
        if (!(n instanceof HTMLElement)) return;
        stack.filter((o) => o === n || n.contains(o)).forEach(release);
      });
    }
  }).observe(document.body, { childList: true, subtree: true });
  document.addEventListener('keydown', onKeyDown);
}
